import { savePostLoginRedirect } from './postLoginRedirect';

export type ClearDataMode = 'cache' | 'allLocalData';
export type ClearAppDataFailureStage = 'cache' | 'logout';

export interface ClearAppDataLocation {
    pathname: string;
    search: string;
}

export interface ClearAppDataResult {
    mode: ClearDataMode;
    cacheCleared: boolean;
    logoutAttempted: boolean;
    logoutSucceeded: boolean | null;
}

export interface ClearAppDataOptions {
    mode: ClearDataMode;
    clearCache: () => Promise<void>;
    logout?: () => void | Promise<void>;
    reloadPage?: () => void;
    currentLocation?: ClearAppDataLocation;
    onError?: (stage: ClearAppDataFailureStage, error: unknown) => void;
}

export const APP_PREFERENCE_LOCAL_STORAGE_KEYS = [
    'operationListColumns',
    'operationListColumnWidths',
    'operationListColumnOrder',
    'operationListSortColumn',
    'isOperationListSortDesc',
    'activeOperationsFilters',
    'isSortedEventListAsc',
    'mySectionsList',
    'selectedFeatures',
    'selectedRole',
    'isSortedVoyageListDesc',
    'isSortedVoyageListAsc',
] as const;

// These keys are application-owned transient state. MSAL keys are intentionally absent:
// provider logout owns auth-cache cleanup, while cache mode must preserve authentication.
export const APP_TRANSIENT_LOCAL_STORAGE_KEYS = [
    'signalRConnection',
    'offlineSinceDateTime',
    'orcaweb_maintenance_status',
    'postponedVersion',
] as const;

export const APP_TRANSIENT_SESSION_STORAGE_KEYS = [
    'lastStowageParams',
    'orcaBaselineVersion',
    'versionChangeDetected',
    'externalIdSignupStatus',
    'adminActiveTab',
    'selectedResolution',
    'signalRDiagnosticSessionId',
    'userChangedFilter',
    'userChangedVessel',
] as const;

export const APP_CONTEXT_LOCAL_STORAGE_KEYS = ['selectedShipImoNumber'] as const;

const removeStorageKeys = (storage: Storage, keys: readonly string[]): void => {
    keys.forEach((key) => storage.removeItem(key));
};

const clearApplicationStorage = (mode: ClearDataMode): void => {
    removeStorageKeys(localStorage, APP_TRANSIENT_LOCAL_STORAGE_KEYS);
    removeStorageKeys(sessionStorage, APP_TRANSIENT_SESSION_STORAGE_KEYS);

    if (mode === 'allLocalData') {
        removeStorageKeys(localStorage, APP_PREFERENCE_LOCAL_STORAGE_KEYS);
    }
};

const getCurrentLocation = (): ClearAppDataLocation => ({
    pathname: window.location.pathname,
    search: window.location.search,
});

/**
 * Clears application-owned cache/data without touching provider auth caches or IndexedDB.
 * Full local-data clearing stores the exact current URL for provider-specific re-authentication.
 */
export const clearAppData = async ({
    mode,
    clearCache,
    logout,
    reloadPage = () => window.location.reload(),
    currentLocation = getCurrentLocation(),
    onError,
}: ClearAppDataOptions): Promise<ClearAppDataResult> => {
    const providerLogout = logout;

    if (mode === 'allLocalData' && !providerLogout) {
        throw new Error('[ClearAppData] A provider logout function is required for allLocalData mode.');
    }

    let cacheCleared = true;
    try {
        await clearCache();
    } catch (error) {
        cacheCleared = false;
        console.error('[ClearAppData] Service-worker cache clearing failed; continuing with the requested data action.', error);
        onError?.('cache', error);
    }

    clearApplicationStorage(mode);

    if (mode === 'allLocalData') {
        savePostLoginRedirect(currentLocation.pathname, currentLocation.search);

        try {
            await providerLogout();
            return {
                mode,
                cacheCleared,
                logoutAttempted: true,
                logoutSucceeded: true,
            };
        } catch (error) {
            console.error('[ClearAppData] Provider logout failed after local-data clearing.', error);
            onError?.('logout', error);
            return {
                mode,
                cacheCleared,
                logoutAttempted: true,
                logoutSucceeded: false,
            };
        }
    }

    reloadPage();
    return {
        mode,
        cacheCleared,
        logoutAttempted: false,
        logoutSucceeded: null,
    };
};
